import { Routes } from '@angular/router';
import { TitleResolver } from '@app/resolvers/admin-title.resolver';
import { stepChallengeResolver } from '@app/resolvers/step-challenge.resolver';
import { AccountContainerComponent } from './features/account/account-container.component';
import { AccountComponent } from './features/account/account/account.component';
import { ManageComponent } from './features/account/manage/manage.component';
import { ResourcesComponent } from './features/account/resources/resources.component';
import { SupportComponent } from './features/account/support/support.component';
import { AppConfigComponent } from './features/app-config/app-config.component';
import { ForgotPasswordComponent } from './features/authentication/forgot-password/forgot-password.component';
import {
  InviteCreatePasswordComponent,
  InviteRouteAction,
} from './features/authentication/invite/invite-create-password.component';
import { ResetPasswordComponent } from './features/authentication/reset-password/reset-password.component';
import { SignInComponent } from './features/authentication/sign-in/sign-in.component';
import { ChallengeCreateComponent } from './features/challenges/challenge-create/challenge-create.component';
import { ChallengeDetailsComponent } from './features/challenges/challenge-details/challenge-details.component';
import { ChallengeLeaderboardComponent } from './features/challenges/challenge-live-details/challenge-leaderboard/challenge-leaderboard.component';
import { ChallengeLiveDetailsComponent } from './features/challenges/challenge-live-details/challenge-live-details.component';
import { ChallengesSelectComponent } from './features/challenges/challenges-select/challenges-select.component';
import { ChallengesComponent } from './features/challenges/challenges.component';
import { StepChallengeCreateComponent } from './features/challenges/step-challenge/step-challange-create/step-challenge-create.component';
import { StepChallengeViewComponent } from './features/challenges/step-challenge/step-challange-view/step-challenge-view.component';
import { StepChallengeEditComponent } from './features/challenges/step-challenge/step-challenge-edit/step-challenge-edit.component';
import { StepChallengeLeaderboardComponent } from './features/challenges/step-challenge/step-challenge-leaderboard/step-challenge-leaderboard.component';
import { HomeComponent } from './features/home/home.component';
import { AdvancedComponent } from './features/tenant-configuration/advanced/advanced.component';
import { ContractsComponent } from './features/tenant-configuration/contracts/contracts.component';
import { GeneralComponent } from './features/tenant-configuration/general/general.component';
import { TenantConfigurationComponent } from './features/tenant-configuration/tenant-configuration.component';
import { TenantFeaturesComponent } from './features/tenant-configuration/tenant-features/tenant-features.component';
import { UsageComponent } from './features/usage/usage.component';
import { UsersComponent } from './features/users/users.component';
import { AdminBootstrapResolver } from './resolvers/admin-bootstrap.resolver';
import { AuthenticationLayoutComponent } from './shared/components/layouts/authentication-layout/authentication-layout.component';
import { FeatureLayoutComponent } from './shared/components/layouts/feature-layout/feature-layout.component';
import {
  authChildGuard,
  authRouteGuard,
  homeRouteGuard,
} from './shared/guards';
import { saveChangesGuard } from './shared/guards/save-changes.guard';
import { superAdminChildGuard } from './shared/guards/super-admin.guard';

export const routes: Routes = [
  {
    path: '',
    pathMatch: 'full',
    canActivate: [authRouteGuard],
    runGuardsAndResolvers: 'always',
  },
  {
    path: '',
    canActivateChild: [authChildGuard],
    component: AuthenticationLayoutComponent,
    children: [
      {
        path: 'login',
        component: SignInComponent,
        data: {
          title: 'navigation.login',
        },
        resolve: { title: TitleResolver },
      },
      {
        path: 'forgot-password',
        component: ForgotPasswordComponent,
        data: { title: 'navigation.forgotPassword' },
        resolve: { title: TitleResolver },
      },
      {
        path: 'reset-password',
        component: ResetPasswordComponent,
        data: { title: 'navigation.resetPassword' },
        resolve: { title: TitleResolver },
      },
      {
        path: 'reset-password/:id',
        component: ResetPasswordComponent,
        data: { title: 'navigation.resetPassword' },
        resolve: { title: TitleResolver },
      },
      {
        path: 'join/:authId/:authCode/:userId/:tenantId',
        pathMatch: 'full',
        component: InviteCreatePasswordComponent,
        data: {
          title: 'navigation.join',
          action: InviteRouteAction.updatePassword,
        },
        resolve: { title: TitleResolver },
      },
      {
        path: 'join/:authCode',
        pathMatch: 'full',
        component: InviteCreatePasswordComponent,
        data: {
          title: 'navigation.join',
        },
        resolve: { title: TitleResolver },
      },
    ],
  },
  {
    path: '',
    canActivateChild: [authChildGuard],
    component: FeatureLayoutComponent,
    resolve: {
      initialData: AdminBootstrapResolver,
    },
    children: [
      {
        path: 'home',
        component: HomeComponent,
        canActivate: [homeRouteGuard],
        data: {
          title: 'navigation.home',
        },
        resolve: { title: TitleResolver },
      },
      {
        path: 'challenges',
        pathMatch: 'full',
        component: ChallengesComponent,
        data: {
          title: 'navigation.challenges',
        },
        resolve: { title: TitleResolver },
      },
      {
        path: 'challenges/select',
        pathMatch: 'full',
        component: ChallengesSelectComponent,
        data: {
          title: 'navigation.challengesSelect',
        },
        resolve: { title: TitleResolver },
      },
      {
        path: 'challenges/select/:id',
        pathMatch: 'full',
        component: ChallengeDetailsComponent,
        data: {
          title: 'navigation.challengesSelect',
        },
        resolve: { title: TitleResolver },
      },
      {
        path: 'challenges/create/steps',
        pathMatch: 'full',
        canDeactivate: [saveChangesGuard],
        component: StepChallengeCreateComponent,
        data: {
          title: 'navigation.createChallenge',
        },
        resolve: { title: TitleResolver },
      },
      {
        path: 'challenges/steps/:id/edit',
        pathMatch: 'full',
        canDeactivate: [saveChangesGuard],
        component: StepChallengeEditComponent,
        data: {
          title: 'navigation.editChallenge',
        },
        resolve: { title: TitleResolver, challenge: stepChallengeResolver },
      },
      {
        path: 'challenges/steps/:id',
        pathMatch: 'full',
        component: StepChallengeViewComponent,
        data: {
          title: 'navigation.viewChallenge',
          mode: 'view',
        },
        resolve: { title: TitleResolver },
      },
      {
        path: 'challenges/steps/:id/leaderboard/:teamId',
        pathMatch: 'full',
        component: StepChallengeLeaderboardComponent,
        data: {
          title: 'navigation.viewChallenge',
          mode: 'view',
        },
        resolve: { title: TitleResolver },
      },
      {
        path: 'challenges/create/:id',
        pathMatch: 'full',
        canDeactivate: [saveChangesGuard],
        component: ChallengeCreateComponent,
        data: {
          title: 'navigation.createChallenge',
          mode: 'create',
        },
        resolve: { title: TitleResolver },
      },
      {
        path: 'challenges/duplicate/:id',
        pathMatch: 'full',
        canDeactivate: [saveChangesGuard],
        component: ChallengeCreateComponent,
        data: {
          title: 'navigation.duplicateChallenge',
          mode: 'duplicate',
        },
        resolve: { title: TitleResolver },
      },
      {
        path: 'challenges/edit/:id',
        pathMatch: 'full',
        canDeactivate: [saveChangesGuard],
        component: ChallengeCreateComponent,
        data: {
          title: 'navigation.editChallenge',
          mode: 'edit',
        },
        resolve: { title: TitleResolver },
      },
      {
        path: 'challenges/live/:id',
        pathMatch: 'full',
        component: ChallengeLiveDetailsComponent,
        data: {
          title: 'navigation.challengeLive',
        },
        resolve: { title: TitleResolver },
      },
      {
        path: 'challenges/live/:id/leaderboard/:teamId',
        pathMatch: 'full',
        component: ChallengeLeaderboardComponent,
        data: {
          title: 'navigation.challengeLive',
        },
        resolve: { title: TitleResolver },
      },
      {
        path: 'users',
        pathMatch: 'full',
        component: UsersComponent,
        data: {
          title: 'navigation.users',
        },
        resolve: { title: TitleResolver },
      },
      {
        path: 'usage',
        pathMatch: 'full',
        component: UsageComponent,
        data: {
          title: 'navigation.usage',
        },
        resolve: { title: TitleResolver },
      },
      {
        path: 'app-config',
        pathMatch: 'full',
        canDeactivate: [saveChangesGuard],
        component: AppConfigComponent,
        data: {
          title: 'navigation.appConfig',
        },
        resolve: { title: TitleResolver },
      },
      {
        path: 'account',
        component: AccountContainerComponent,
        children: [
          {
            path: '',
            component: AccountComponent,
            data: {
              title: 'navigation.account',
            },
            resolve: { title: TitleResolver },
          },
          {
            path: 'manage',
            component: ManageComponent,
            data: {
              title: 'navigation.accountManage',
            },
            resolve: { title: TitleResolver },
          },
          {
            path: 'support',
            component: SupportComponent,
            data: {
              title: 'navigation.accountSupport',
            },
            resolve: { title: TitleResolver },
          },
          {
            path: 'resources',
            component: ResourcesComponent,
            data: {
              title: 'navigation.accountResources',
            },
            resolve: { title: TitleResolver },
          },
        ],
      },
      {
        path: 'tenant-configuration',
        pathMatch: 'full',
        redirectTo: 'tenant-configuration/general',
      },
      {
        path: 'tenant-configuration',
        component: TenantConfigurationComponent,
        canActivateChild: [superAdminChildGuard],
        children: [
          {
            path: 'general',
            component: GeneralComponent,
            canDeactivate: [saveChangesGuard],
            data: {
              title: 'navigation.tenantConfigurationGeneral',
            },
            resolve: { title: TitleResolver },
          },
          {
            path: 'features',
            component: TenantFeaturesComponent,
            canDeactivate: [saveChangesGuard],
            data: {
              title: 'navigation.tenantConfigurationFeatures',
            },
            resolve: { title: TitleResolver },
          },
          {
            path: 'contracts',
            component: ContractsComponent,
            canDeactivate: [saveChangesGuard],
            data: {
              title: 'navigation.tenantConfigurationContracts',
            },
            resolve: { title: TitleResolver },
          },
          {
            path: 'advanced',
            component: AdvancedComponent,
            canDeactivate: [saveChangesGuard],
            data: {
              title: 'navigation.tenantConfigurationAdvanced',
            },
            resolve: { title: TitleResolver },
          },
        ],
      },
    ],
  },
  {
    path: '',
    pathMatch: 'full',
    redirectTo: 'home',
  },
  { path: '**', redirectTo: '/login' },
];
